/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";

const DATA_TYPES = ["boolean", "int", "varchar"];

function SettingModal({ open, onClose, onSubmit, initialData }) {
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [dataType, setDataType] = useState("varchar");
  const [error, setError] = useState("");

  useEffect(() => {
    if (initialData) {
      setKey(initialData.key);
      setValue(initialData.value);
      setDataType(initialData.dataType);
    } else {
      setKey("");
      setValue("");
      setDataType("varchar");
    }
    setError("");
  }, [initialData]);

  // ------------------------------
  //  🔒 Disable SUBMIT until valid
  // ------------------------------
  const isSubmitDisabled =
    (!initialData && !key.trim()) || value === "" || value === null;

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    setError("");
    onSubmit({ key, value, dataType });
    onClose();
  };

  const renderValueInput = () => {
    switch (dataType) {
      case "boolean":
        return (
          <Select
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{
              borderRadius: "12px",
              mt: 1,
            }}
          >
            <MenuItem value="true">true</MenuItem>
            <MenuItem value="false">false</MenuItem>
          </Select>
        );

      case "int":
        return (
          <TextField
            fullWidth
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          />
        );

      default:
        return (
          <TextField
            fullWidth
            value={value}
            placeholder="Enter text value"
            onChange={(e) => setValue(e.target.value)}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          />
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0px 10px 40px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          bgcolor: "#2F80ED",
          color: "white",
          p: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <SettingsIcon sx={{ fontSize: 40 }} />
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          {initialData ? "Edit Setting" : "Add Setting"}
        </Typography>
      </Box>

      {/* CONTENT */}
      <DialogContent sx={{ p: 3, bgcolor: "#F7FAFF" }}>
        {!initialData && (
          <TextField
            fullWidth
            label="Key"
            placeholder="Enter key name"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            margin="normal"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          />
        )}

        <FormControl fullWidth margin="normal">
          <InputLabel>Data Type</InputLabel>
          <Select
            value={dataType}
            label="Data Type"
            disabled={!!initialData}
            onChange={(e) => setDataType(e.target.value)}
            sx={{
              borderRadius: "12px",
            }}
          >
            {DATA_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography sx={{ mt: 2, mb: 0.5, fontWeight: 500 }}>Value</Typography>
        {renderValueInput()}

        {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
      </DialogContent>

      {/* FOOTER */}
      <DialogActions
        sx={{
          p: 3,
          justifyContent: "center",
          gap: 2,
          bgcolor: "#fff",
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: "12px",
            px: 4,
            borderColor: "#2F80ED",
            color: "#2F80ED",
            fontWeight: 600,
            "&:hover": { borderColor: "#2F80ED" },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{
            borderRadius: "12px",
            px: 4,
            bgcolor: isSubmitDisabled ? "#9BBEF4" : "#2F80ED",
            fontWeight: 600,
            cursor: isSubmitDisabled ? "not-allowed" : "pointer",
            "&:hover": {
              bgcolor: isSubmitDisabled ? "#9BBEF4" : "#1D6FE0",
            },
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SettingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    key: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
    ]),
    dataType: PropTypes.string,
  }),
};

export default SettingModal;
